import { Capacitor } from "@capacitor/core";
import { checkClaimsWeb, IOptionsWithTypes, SectionId, spErrorTags, spLogTags } from "@eventr/common";
import { ColoursObj, TDialogStore, useDialogStore } from "@eventr/common-storybook";
import { isNativeNavigationAvailable, NativeNavigation } from "@eventr/native/navigation";
import { EditChannelUsersBodyType } from "data/Rocket";
import { quickJoinSectionItem } from "feat/editItinerary/quickJoinSectionItem";
import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import { addUsersToChannel, createChannel, useRocketChatContext } from "feat/rocketchat/rocketChatProvider";
import { useViewedUserId } from "feat/viewedUser/viewedUser";
import { useHasFeature } from "hooks/useHasFeature";
import i18n from "i18n/i18next";
import { useMemo, useState } from "react";
import { bs, useBoundStore } from "state/bound.store";
import { useOverlayStore } from "state/overlayState/overlay.store";
import { defaultAddSnack } from "state/overlayState/overlayContext";
import { getLiveToken } from "state/rocketchatState/rocketchatUi.store";
import { enableChat, Utils } from "utils";
import { useObjectContext } from "v2/componentCards/providers/objectProvider";
import { useSectionV2Context } from "v2/state/sectionProvider";
import { useShallow } from "zustand/react/shallow";
import { useChannelForSectionItem } from "./use-channel-for-section-item";
import { ISectionItemChat, TSectionItemChatState } from "./section-item-chat";

export interface ISectionItemChatOptions {
  /** Joined into the room name when the room is created. */
  names: string[];
  users: IOptionsWithTypes[];
  /** The id the room hangs off: the item, or one car of a parking item. */
  _id: string;
  /** The item to join, when it differs from `_id`. */
  editID?: string;
  sectionId?: SectionId;
  /** Falls back to the enclosing object context. */
  visibility?: string;
}

/** One item's chat room: its state for the viewer, and the actions that open or join it. */
export const useSectionItemChat = ({
  names,
  users,
  _id,
  editID,
  sectionId = SectionId.ACTIVITY,
  visibility,
}: ISectionItemChatOptions): ISectionItemChat => {
  const { pushPath } = useGlobalRouter();
  const [isJoining, setIsJoining] = useState(false);
  const { sectionData, isPublic } = useSectionV2Context();
  const objectContext = useObjectContext();
  const visibilityToUse = visibility ?? objectContext.visibility;
  const { replaceDialog, clearDialog } = useDialogStore(
    useShallow((s: TDialogStore) => ({
      clearDialog: s.clearDialog,
      replaceDialog: s.replaceDialog,
    })),
  );
  const sectionIdToUse = sectionData?.sectionID ?? sectionId;

  const viewedUserId = useViewedUserId();
  const { orgConfig, wpUser, isUserPartOfSelection, selectedItinID } = useBoundStore(
    useShallow((s) => ({
      orgConfig: s.orgConfig,
      wpUser: s.wpUser,
      isUserPartOfSelection: s.isUserPartOfSelection,
      selectedItinID: s.selectedItinID,
    })),
  );
  const hasFeature = useHasFeature("Chat");

  const { openChat, userRooms, userSubscriptions, isActivated } = useRocketChatContext();

  const { channel, channelRoom } = useChannelForSectionItem(_id);
  const isGuest = isUserPartOfSelection(users);

  const roomState = useMemo((): TSectionItemChatState => {
    if (!isActivated || orgConfig.disableChat) {
      return "inactive";
    }
    if (!userRooms || !userSubscriptions) {
      return "loading";
    }
    if (!channel) {
      return "unjoined";
    }
    const sub = userSubscriptions.find((sub) => sub.rid === channel.channel_id);
    if (sub?.alert) {
      return "unread";
    }
    return sub || channelRoom ? "read" : "unjoined";
  }, [channel, channelRoom, userRooms, userSubscriptions, isActivated, orgConfig]);

  const state: TSectionItemChatState = users.length === 0 || !enableChat() || !hasFeature ? "hidden" : roomState;

  const canJoin = useMemo(() => {
    if (viewedUserId !== null) return false;
    return (
      checkClaimsWeb(["/"], ["editItinerary"]) &&
      !orgConfig?.editInAppDisabled &&
      !isPublic &&
      visibilityToUse?.toLowerCase() !== "public"
    );
  }, [viewedUserId, orgConfig?.editInAppDisabled, isPublic, visibilityToUse]);

  const createOrNavigateToChannel = () => {
    if (channel && channel?.members?.includes(wpUser?.id!)) {
      openChat(channel?.channel_id, (channelRoom?.t ?? "c") + channel.section_id);
      return;
    }
    if (names.length === 0 || !sectionIdToUse) return;

    useOverlayStore.getState().setLoading(true);
    const u = users.filter((u) => u.type === "User");
    createChannel({
      channel_name_parts: names,
      itinerary_id: selectedItinID,
      section_id: sectionIdToUse,
      // The WP side adds only the ids it is handed, and the creator is not necessarily an
      // assignee of the item. Without this they land in a room they hold no subscription to,
      // which 400s every markAsRead.
      user_ids: [...new Set([...u.map((u) => u?.option as number), ...(wpUser ? [wpUser.id] : [])])],
      _id: _id,
    })
      .then((c) => {
        if (c?.status === "success") {
          if (c?.data?.channel_id) {
            openChat(c.data.channel_id, "c" + sectionIdToUse);
            bs().pullRocketChatChannels();
          } else if (c?.identifier && wpUser) {
            try {
              const data: EditChannelUsersBodyType = {
                user_ids: [wpUser.id.toString()],
                channel_id: c.identifier,
              };
              addUsersToChannel(data).then((resp) => {
                const result = resp?.status === "success" && resp?.data?.length === 1 ? resp.data[0] : undefined;
                if (result?.status === "success" && result?.channel_id) {
                  openChat(result.channel_id, "c" + sectionIdToUse);
                  bs().pullRocketChatChannels();
                } else {
                  spLogTags("Failed to join chat " + c?.identifier, ["ERROR", "rocketchat"], resp);
                }
              });
            } catch (error) {
              spLogTags("Failed to join chat " + c?.identifier, ["ERROR", "rocketchat"], error);
            }
          }
        } else if (c.status === "error") {
          spLogTags("Error creating channel", ["rocketchat", "ERROR"]);
          spLogTags(c, ["rocketchat", "ERROR"]);
        } else {
          spLogTags(c, ["rocketchat", "ERROR"]);
        }
      })
      .finally(() => {
        useOverlayStore.getState().setLoading(false);
      });
  };

  const open = () => {
    Utils.hapticFeedback("light");
    if (state === "loading" || state === "hidden") return;
    if (getLiveToken() !== undefined) {
      createOrNavigateToChannel();
    } else {
      pushPath("/tabs/rocketchat");
    }
  };

  const handleDialogConfirm = () => {
    clearDialog();
    setIsJoining(true);
    quickJoinSectionItem(sectionIdToUse, editID ?? _id, pushPath)
      .then((success) => {
        if (!success) {
          defaultAddSnack(i18n.t("general.unexpected-error"), "error");
        }
      })
      .catch((error) => {
        spLogTags("Error joining section item", ["ERROR"], error);
        defaultAddSnack(i18n.t("general.unexpected-error"), "error");
      })
      .finally(() => {
        setIsJoining(false);
      });
  };

  const join = () => {
    const text = i18n.t("edit-itinerary.joinButtonConfirmationText." + sectionIdToUse);
    const header = i18n.t("edit-itinerary.joinButtonText." + sectionIdToUse);
    const cancelText = i18n.t("info-dialog.no");
    const confirmText = i18n.t("info-dialog.yes");

    // MobileDialog's native-confirm routing mismatches for a view pushed inside a tab's own
    // stack, silently no-oping instead of presenting anything. presentConfirmDialog takes no
    // target/id, so it presents correctly regardless of which stack the caller is in.
    if (Capacitor.getPlatform() === "ios" && isNativeNavigationAvailable()) {
      NativeNavigation.presentConfirmDialog({ header, text, cancelText, confirmText })
        .then(({ confirmed }) => {
          if (confirmed) handleDialogConfirm();
        })
        .catch((error) => {
          spErrorTags("Failed to present native confirm dialog", ["QUICK_JOIN"], error);
          defaultAddSnack(i18n.t("general.unexpected-error"), "error");
        });
      return;
    }

    replaceDialog({
      kind: "confirm",
      text,
      header,
      confirmBtnTextColour: ColoursObj.core.secondary,
      cancelBtnTextColour: ColoursObj.states.error,
      onConfirm: handleDialogConfirm,
      onCancel: () => {
        clearDialog();
      },
      cancelText,
      confirmText,
      backdropClosable: false,
    });
  };

  return { state, isGuest, canJoin, isJoining, open, join };
};
